import { Slot } from '../Slot'
import type { VariantProps } from '@joyful/utils'
import { cn, tv } from '@joyful/utils'
import type { ComponentProps } from 'react'
import type { SetRequired } from 'type-fest'

export const headingVariants = tv({
  base: 'font-semibold leading-tight text-tremor-content-strong',

  compoundVariants: [
    {
      class: 'text-2xl/tight tracking-wide pb-1',
      level: 1,
      variant: 'title',
    },
    {
      class: 'text-2xl/tight tracking-wide pb-1',
      level: 2,
      variant: 'title',
    },
    {
      class: 'text-xl/tight tracking-wide',
      level: 3,
      variant: 'title',
    },
    {
      class: 'text-base/tight',
      level: 4,
      variant: 'title',
    },
    {
      class: 'text-sm/tight',
      level: 5,
      variant: 'title',
    },
    {
      class: 'text-[0.85rem]/tight text-tremor-content',
      level: 6,
      variant: 'title',
    },
  ],

  defaultVariants: {
    margins: 'default',
    variant: 'title',
  },

  variants: {
    level: {
      1: null,
      2: null,
      3: null,
      4: null,
      5: null,
      6: null,
    },

    margins: {
      default: 'mt-6 mb-4',
      none: 'my-0',
    },

    variant: {
      subtitle: 'text-base/7 sm:text-sm/6',
      title: null,
    },
  },
})

export interface HeadingProps
  extends
    Omit<ComponentProps<'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'>, 'className'>,
    SetRequired<VariantProps<typeof headingVariants>, 'level'> {
  /**
   * @default false
   */
  asChild?: boolean
}

export function Heading({
  asChild = false,
  level,
  margins,
  variant,
  ...restProps
}: HeadingProps) {
  // SAFETY: Slot parents inject className at runtime onto HeadingProps which omits it.
  const { className: injectedClassName, ...domProps } =
    restProps as HeadingProps & {
      className?: string
    }
  const className = cn(
    headingVariants({
      level,
      margins,
      variant,
    }),
    injectedClassName,
  )

  if (asChild) {
    return <Slot className={className} {...domProps} />
  }

  const Element: `h${typeof level}` = `h${level}`

  return <Element className={className} {...domProps} />
}

export interface SubheadingProps extends Omit<
  HeadingProps,
  'level' | 'margins' | 'variant'
> {
  level?: HeadingProps['level']
}

export function Subheading({ level = 2, ...props }: SubheadingProps) {
  return <Heading {...props} level={level} margins="none" variant="subtitle" />
}
